<template>
    <UserLayout :page-title="t('referralLevel')" :page-section="t('network')">
        <div class="rlv-wrap">
            <div class="rlv-inner">

                <!-- Welcome Strip -->
                <div class="rlv-welcome anim-up" style="--d:0ms">
                    <div class="rlvw-left">
                        <div class="rlvw-icon">
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M13 10V3L4 14h7v7l9-11h-7z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="rlvw-eyebrow">{{ t('network') }}</p>
                            <h1 class="rlvw-title">{{ t('referralLevel') }}</h1>
                        </div>
                    </div>
                    <div class="rlvw-chips">
                        <div class="rlvw-chip rlvw-chip--hi">
                            <span class="rlvw-chip-val">{{ currentLevel.commission_rate }}%</span>
                            <span class="rlvw-chip-lbl">{{ t('currentRate') }}</span>
                        </div>
                        <div class="rlvw-chip">
                            <span class="rlvw-chip-val">{{ userStats.activeReferrals }}</span>
                            <span class="rlvw-chip-lbl">{{ t('active') }}</span>
                        </div>
                        <div class="rlvw-chip rlvw-chip--blue">
                            <span class="rlvw-chip-val">{{ t('level') }} {{ currentLevel.level }}</span>
                            <span class="rlvw-chip-lbl">{{ t('currentLevel') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Current Level Hero -->
                <div class="rlv-hero anim-up" style="--d:60ms">
                    <div class="rlv-hero-glow"></div>
                    <div class="rlv-hero-head">
                        <div>
                            <p class="rlv-hero-eyebrow">{{ t('active') }}</p>
                            <h2 class="rlv-hero-title">{{ t('currentLevel') }}</h2>
                            <p class="rlv-hero-sub">{{ t('yourActiveCommissionRate') }}</p>
                        </div>
                        <div class="rlv-hero-badge">{{ t('level') }} {{ currentLevel.level }}</div>
                    </div>

                    <div class="rlv-hero-rate">
                        <span class="rlv-rate-num">{{ currentLevel.commission_rate }}%</span>
                        <span class="rlv-rate-lbl">{{ t('commission') }}</span>
                    </div>

                    <div class="rlv-hero-stats">
                        <div class="rlv-hero-stat">
                            <p class="rlv-hs-lbl">{{ t('yourReferrals') }}</p>
                            <p class="rlv-hs-val">{{ userStats.activeReferrals }}</p>
                        </div>
                        <div class="rlv-hero-stat">
                            <p class="rlv-hs-lbl">{{ t('teamInvestment') }}</p>
                            <p class="rlv-hs-val">{{ currencySymbol }}{{ formatCurrency(userStats.totalInvestment) }}</p>
                        </div>
                    </div>
                </div>

                <!-- Progress Cards -->
                <div class="rlv-progress anim-up" style="--d:160ms">

                    <!-- Previous Level -->
                    <div v-if="previousLevel" class="rlv-card rlv-card--done">
                        <div class="rlv-card-head">
                            <div>
                                <p class="rlv-card-eyebrow">{{ t('level') }} {{ previousLevel.level }}</p>
                                <h3 class="rlv-card-title">{{ t('previousLevel') }}</h3>
                                <p class="rlv-card-sub">{{ t('completedMilestone') }}</p>
                            </div>
                            <div class="rlv-done-badge">
                                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                                </svg>
                                {{ t('completed') }}
                            </div>
                        </div>

                        <div class="rlv-prev-rate">{{ previousLevel.commission_rate }}%</div>
                        <p class="rlv-prev-lbl">{{ t('commission') }}</p>

                        <div class="rlv-req-grid">
                            <div class="rlv-req-item">
                                <p class="rlv-req-lbl">{{ t('requiredReferrals') }}</p>
                                <p class="rlv-req-val">{{ previousLevel.min_referrals_required }}</p>
                            </div>
                            <div class="rlv-req-item">
                                <p class="rlv-req-lbl">{{ t('requiredInvestment') }}</p>
                                <p class="rlv-req-val">{{ currencySymbol }}{{ formatCurrency(previousLevel.min_investment_required) }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Next Level -->
                    <div v-if="nextLevel" class="rlv-card" :class="{ 'rlv-card--full': !previousLevel }">
                        <div class="rlv-card-head">
                            <div>
                                <p class="rlv-card-eyebrow">{{ t('level') }} {{ nextLevel.level }}</p>
                                <h3 class="rlv-card-title">{{ t('nextLevel') }}</h3>
                                <p class="rlv-card-sub">{{ t('unlockHigherCommission') }}</p>
                            </div>
                            <div class="rlv-inprog-badge">{{ t('inProgress') }}</div>
                        </div>

                        <div class="rlv-next-rate">{{ nextLevel.commission_rate }}%</div>
                        <p class="rlv-next-lbl">{{ t('targetCommission') }}</p>

                        <div class="rlv-prog-rows">
                            <!-- Referrals Progress -->
                            <div class="rlv-prog-row">
                                <div class="rlv-prog-label-row">
                                    <span class="rlv-prog-lbl">{{ t('referralsProgress') }}</span>
                                    <span class="rlv-prog-count">{{ userStats.activeReferrals }} / {{ nextLevel.min_referrals_required }}</span>
                                </div>
                                <div class="rlv-prog-track">
                                    <div class="rlv-prog-fill shimmer-bar" :style="`width:${referralsProgress}%`"></div>
                                </div>
                                <p class="rlv-prog-hint">{{ referralsRemaining }} {{ t('moreReferralsNeeded') }}</p>
                            </div>

                            <!-- Investment Progress -->
                            <div class="rlv-prog-row">
                                <div class="rlv-prog-label-row">
                                    <span class="rlv-prog-lbl">{{ t('investmentProgress') }}</span>
                                    <span class="rlv-prog-count">{{ currencySymbol }}{{ formatCurrency(userStats.totalInvestment) }} / {{ currencySymbol }}{{ formatCurrency(nextLevel.min_investment_required) }}</span>
                                </div>
                                <div class="rlv-prog-track">
                                    <div class="rlv-prog-fill shimmer-bar" :style="`width:${investmentProgress}%`"></div>
                                </div>
                                <p class="rlv-prog-hint">{{ currencySymbol }}{{ formatCurrency(investmentRemaining) }} {{ t('moreInvestmentNeeded') }}</p>
                            </div>
                        </div>

                        <div class="rlv-req-note">
                            <p class="rlv-req-note-title">{{ t('requirementsToUnlock') }}</p>
                            <p class="rlv-req-note-text">
                                {{ t('refer') }} {{ nextLevel.min_referrals_required }} {{ t('activeMembersAnd') }}
                                {{ t('achieve') }} {{ currencySymbol }}{{ formatCurrency(nextLevel.min_investment_required) }}
                                {{ t('inTeamInvestment') }}
                            </p>
                        </div>
                    </div>

                    <!-- Max Level -->
                    <div v-if="!nextLevel" class="rlv-card rlv-card--max">
                        <div class="rlv-max-icon">
                            <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                      d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" opacity=".3"/>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                            </svg>
                        </div>
                        <h3 class="rlv-max-title">{{ t('maxLevelReached') }}</h3>
                        <p class="rlv-max-sub">{{ t('congratulationsMaxLevel') }}</p>
                    </div>

                </div>

            </div>
        </div>
    </UserLayout>
</template>

<script setup>
import {computed, onMounted} from 'vue';
import {usePage} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useTranslation} from "@/composables/useTranslation.js";

const page = usePage();
const {t} = useTranslation();

const props = defineProps({
    allLevels: Array,
    userStats: Object,
});


const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172};
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const currentLevel = computed(() => {
    const sortedLevels = [...props.allLevels].sort((a, b) => b.level - a.level);

    for (const level of sortedLevels) {
        if (props.userStats.activeReferrals >= level.min_referrals_required &&
            props.userStats.totalInvestment >= level.min_investment_required) {
            return level;
        }
    }

    return props.allLevels[0];
});

const previousLevel = computed(() => {
    if (currentLevel.value.level === 1) return null;
    return props.allLevels.find(level => level.level === currentLevel.value.level - 1);
});

const nextLevel = computed(() => {
    return props.allLevels.find(level => level.level === currentLevel.value.level + 1);
});

const referralsProgress = computed(() => {
    if (!nextLevel.value) return 100;
    return Math.min((props.userStats.activeReferrals / nextLevel.value.min_referrals_required) * 100, 100);
});

const investmentProgress = computed(() => {
    if (!nextLevel.value) return 100;
    return Math.min((props.userStats.totalInvestment / nextLevel.value.min_investment_required) * 100, 100);
});

const referralsRemaining = computed(() => {
    if (!nextLevel.value) return 0;
    return Math.max(0, nextLevel.value.min_referrals_required - props.userStats.activeReferrals);
});

const investmentRemaining = computed(() => {
    if (!nextLevel.value) return 0;
    return Math.max(0, nextLevel.value.min_investment_required - props.userStats.totalInvestment);
});

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) console.log(flash.success);
        if (flash?.error) console.error(flash.error);
        if (flash?.warning) console.warn(flash.warning);
        if (flash?.info) console.info(flash.info);
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.rlv-wrap { width:100%; }
.rlv-inner { max-width:1100px; margin:0 auto; padding:20px 16px; display:flex; flex-direction:column; gap:14px; }
@media(min-width:768px) { .rlv-inner { padding:24px; gap:16px; } }
@media(min-width:1024px) { .rlv-inner { padding:28px 32px; } }

/* Welcome */
.rlv-welcome { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:16px; padding:16px 20px; display:flex; align-items:center; justify-content:space-between; gap:12px; position:relative; overflow:hidden; flex-wrap:wrap; }
.rlv-welcome::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.rlvw-left { display:flex; align-items:center; gap:12px; }
.rlvw-icon { width:40px; height:40px; border-radius:11px; background:rgba(167,93,237,0.14); border:1px solid rgba(167,93,237,0.26); display:flex; align-items:center; justify-content:center; color:#a75ded; flex-shrink:0; }
.rlvw-eyebrow { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.36); margin:0 0 2px; }
.rlvw-title { font-size:18px; font-weight:700; color:#fff; margin:0; }
.rlvw-chips { display:flex; gap:7px; flex-wrap:wrap; }
.rlvw-chip { background:rgba(167,93,237,0.09); border:1px solid rgba(167,93,237,0.17); border-radius:9px; padding:6px 12px; display:flex; flex-direction:column; align-items:center; }
.rlvw-chip--hi { background:rgba(52,211,153,0.09); border-color:rgba(52,211,153,0.2); }
.rlvw-chip--hi .rlvw-chip-val { color:#34d399; }
.rlvw-chip--blue { background:rgba(59,130,246,0.09); border-color:rgba(59,130,246,0.2); }
.rlvw-chip--blue .rlvw-chip-val { color:#60a5fa; }
.rlvw-chip-val { font-size:13px; font-weight:700; color:#a75ded; }
.rlvw-chip-lbl { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.38); margin-top:2px; }

/* Hero */
.rlv-hero { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.22); border-radius:20px; padding:28px; position:relative; overflow:hidden; }
.rlv-hero::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.5),transparent); }
.rlv-hero-glow { position:absolute; top:-40px; right:-40px; width:200px; height:200px; border-radius:50%; background:radial-gradient(circle,rgba(167,93,237,0.12) 0%,transparent 70%); pointer-events:none; }

.rlv-hero-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:20px; }
.rlv-hero-eyebrow { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.36); margin:0 0 4px; }
.rlv-hero-title { font-size:20px; font-weight:700; color:#fff; margin:0 0 3px; }
.rlv-hero-sub { font-size:12px; color:rgba(255,255,255,0.4); margin:0; }
.rlv-hero-badge { display:inline-flex; padding:6px 14px; border-radius:9px; font-size:12px; font-weight:700; background:rgba(167,93,237,0.18); border:1px solid rgba(167,93,237,0.32); color:#a75ded; flex-shrink:0; }

.rlv-hero-rate { display:flex; align-items:baseline; gap:10px; margin-bottom:20px; }
.rlv-rate-num { font-size:52px; font-weight:900; color:#a75ded; line-height:1; }
.rlv-rate-lbl { font-size:16px; font-weight:600; color:rgba(255,255,255,0.45); }

.rlv-hero-stats { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
@media(min-width:600px) { .rlv-hero-stats { grid-template-columns:200px 200px; } }
.rlv-hero-stat { background:rgba(167,93,237,0.07); border:1px solid rgba(167,93,237,0.13); border-radius:12px; padding:14px; }
.rlv-hs-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:rgba(255,255,255,0.38); margin:0 0 5px; }
.rlv-hs-val { font-size:20px; font-weight:800; color:#fff; margin:0; }

/* Progress Grid */
.rlv-progress { display:grid; gap:14px; }
@media(min-width:768px) { .rlv-progress { grid-template-columns:1fr 1fr; } }

/* Card base */
.rlv-card { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:18px; padding:24px; position:relative; overflow:hidden; }
.rlv-card::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.28),transparent); }
.rlv-card--done { border-color:rgba(52,211,153,0.2); }
.rlv-card--done::before { background:linear-gradient(90deg,transparent,rgba(52,211,153,0.3),transparent); }
.rlv-card--full { grid-column:1/-1; }
.rlv-card--max { text-align:center; padding:50px 24px; }

.rlv-card-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:16px; }
.rlv-card-eyebrow { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.36); margin:0 0 3px; }
.rlv-card-title { font-size:16px; font-weight:700; color:#fff; margin:0 0 3px; }
.rlv-card-sub { font-size:12px; color:rgba(255,255,255,0.38); margin:0; }

.rlv-done-badge { display:inline-flex; align-items:center; gap:5px; padding:5px 11px; border-radius:99px; font-size:11px; font-weight:600; background:rgba(52,211,153,0.12); border:1px solid rgba(52,211,153,0.25); color:#34d399; flex-shrink:0; }
.rlv-inprog-badge { display:inline-flex; padding:5px 11px; border-radius:99px; font-size:11px; font-weight:600; background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.12); color:rgba(255,255,255,0.55); flex-shrink:0; }

.rlv-prev-rate { font-size:36px; font-weight:900; color:rgba(255,255,255,0.25); line-height:1; margin-bottom:3px; }
.rlv-prev-lbl { font-size:12px; color:rgba(255,255,255,0.3); margin:0 0 16px; }

.rlv-next-rate { font-size:36px; font-weight:900; color:#a75ded; line-height:1; margin-bottom:3px; }
.rlv-next-lbl { font-size:12px; color:rgba(167,93,237,0.65); margin:0 0 16px; }

.rlv-req-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.rlv-req-item { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:9px; padding:10px 12px; }
.rlv-req-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.35); margin:0 0 4px; }
.rlv-req-val { font-size:13px; font-weight:700; color:rgba(255,255,255,0.45); margin:0; }

/* Shimmer */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%); animation:shimmer 2s infinite; }
@keyframes shimmer { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

/* Progress bars */
.rlv-prog-rows { display:flex; flex-direction:column; gap:14px; margin-bottom:16px; }
.rlv-prog-row {}
.rlv-prog-label-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:7px; }
.rlv-prog-lbl { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.4); }
.rlv-prog-count { font-size:12px; font-weight:700; color:#a75ded; }
.rlv-prog-track { height:6px; background:rgba(255,255,255,0.07); border-radius:99px; overflow:hidden; margin-bottom:5px; }
.rlv-prog-fill { height:6px; border-radius:99px; background:linear-gradient(90deg,#a75ded,#8845cc); transition:width 1s ease; }
.rlv-prog-hint { font-size:11px; color:rgba(255,255,255,0.32); margin:0; }

.rlv-req-note { background:rgba(167,93,237,0.06); border:1px solid rgba(167,93,237,0.12); border-radius:10px; padding:12px 14px; border-top:1px solid rgba(167,93,237,0.12); }
.rlv-req-note-title { font-size:12px; font-weight:700; color:rgba(255,255,255,0.7); margin:0 0 4px; }
.rlv-req-note-text { font-size:12px; color:rgba(255,255,255,0.38); margin:0; line-height:1.5; }

/* Max Level */
.rlv-max-icon { width:64px; height:64px; border-radius:18px; background:rgba(167,93,237,0.12); border:1px solid rgba(167,93,237,0.22); display:flex; align-items:center; justify-content:center; color:#a75ded; margin:0 auto 16px; }
.rlv-max-title { font-size:20px; font-weight:700; color:#fff; margin:0 0 6px; }
.rlv-max-sub { font-size:13px; color:rgba(255,255,255,0.4); margin:0; }

@media(prefers-reduced-motion:reduce) {
    .anim-up { animation:none; }
    .shimmer-bar::after { animation:none; }
}
</style>
